import { useCreateConversationMessageMutation, type CreateConversationMessagePayload } from "#features/conversations/api/useCreateConversationMessageMutation.ts";
import { cancelPendingConnection } from "#features/conversations/lib/connectionRecords.ts";
import { cancelPendingQuestion } from "#features/conversations/lib/questionRecords.ts";
import { cancelPendingApproval } from "#features/conversations/lib/toolApprovalRecords.ts";
import { type AssistantError, AssistantErrorCodes, getToolNameKey } from "#features/conversations/types/assistantError.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { AiMutationKeys, AiQueryKeys } from "#lib/AiQueryKeys.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button, Flex, useColorSchemeValue, type BorderValue, type ColorValue } from "@hopper-ui/components";
import { WarningIcon } from "@hopper-ui/icons";
import { useMutationState, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, type RefObject } from "react";

interface Props {
    ref: RefObject<HTMLDivElement | null> | null;
    messageId: string;
    error?: AssistantError | null;
}

export const AssistantMessageError = ({ ref, messageId, error }: Props) => {
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const { mutate } = useCreateConversationMessageMutation();
    const queryClient = useQueryClient();

    const errorMessage = useMemo(() => {
        // No structured payload (older backends, network failures) → generic message.
        if (!error?.code) {
            return tShared("errors.conversationError");
        }

        switch (error.code) {
            case AssistantErrorCodes.modelProviderUnavailable:
                return tShared("errors.model_provider_unavailable");
            case AssistantErrorCodes.toolExecutionFailed: {
                if (!error.tool) {
                    return tShared("errors.unknown");
                }

                // Resolve the tool's user-friendly label, falling back to the raw
                // tool name when no localized label exists (e.g. a new backend tool).
                const toolLabel = tShared(getToolNameKey(error.tool), { defaultValue: error.tool });

                return tShared("errors.tool_execution_failed", { tool: toolLabel });
            }
            case AssistantErrorCodes.resumeInProgress:
                return tShared("errors.resume_in_progress");
            case AssistantErrorCodes.unknown:
                return tShared("errors.unknown");
            default:
                // Unknown/future code: fall back to the backend-provided English message.
                return error.message || tShared("errors.conversationError");
        }
    }, [error, tShared]);

    const textColor = useColorSchemeValue(
        "danger",
        "warning-text-weak"
    ) as ColorValue;

    const buttonTextColor = useColorSchemeValue(
        "core_orchid-bloom-700",
        "core_samoyed"
    ) as ColorValue;

    const buttonBorderColor = useColorSchemeValue(
        "core_orchid-bloom-400",
        "core_samoyed"
    ) as BorderValue;

    const mutationPayload = useMutationState({
        filters: {
            mutationKey: AiMutationKeys.CreateConversationMessage(),
            predicate: mutation => {
                const payload = mutation.state.variables as CreateConversationMessagePayload | undefined;

                return payload?.messageId === messageId;
            }
        },
        select: mutation => mutation.state.variables as CreateConversationMessagePayload | undefined
    })?.[0];

    const retry = useCallback(() => {
        if (!mutationPayload) {
            return;
        }

        trackEvent(MixpanelEvent.ButtonClicked, { Location: "Assistant Conversation Answer Error", Trigger: "Retry" });

        const isStaleDecision = (mutationPayload.resume?.length ?? 0) > 0;

        if (isStaleDecision) {
            // The decision may have already resolved server-side despite this error, so refetch rather
            // than resubmit these possibly-stale tool-call ids.
            queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversation(mutationPayload.conversationId) });

            return;
        }

        // A later turn may have paused on an approval, a question or a connection request since; the retried turn supersedes it like any other send.
        const { conversationId, onError } = mutationPayload;
        const hasCancelled = [cancelPendingApproval(queryClient, conversationId), cancelPendingQuestion(queryClient, conversationId), cancelPendingConnection(queryClient, conversationId)].some(Boolean);
        mutate({
            ...mutationPayload,
            abortController: new AbortController(),
            retry: true,
            // Refetch so the approval, question or connection request comes back if the retry never reached core.
            onError: hasCancelled
                ? mutationError => {
                    queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) });
                    onError?.(mutationError);
                }
                : onError
        });
    }, [mutationPayload, mutate, trackEvent, queryClient]);

    return (
        <Flex
            ref={ref}
            direction="column"
            gap="inline-md"
        >
            <Flex
                direction="column"
                gap="inline-md"
                backgroundColor="danger-weak"
                padding="inset-md"
                borderRadius="pill"
                wordBreak="break-word"
                className="wai-assistant-message-error"
            >
                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    gap="inline-md"
                    fontSize="body-sm"
                    color={textColor}
                    wrap="wrap"
                >
                    <Flex
                        alignItems="center"
                        gap="inline-sm"
                    >
                        <WarningIcon
                            flexShrink={0}
                            fill="currentcolor"
                            size="sm"
                        />
                        {errorMessage}
                    </Flex>
                    {mutationPayload &&
                        <Button
                            aria-label={tShared("retry")}
                            variant="secondary"
                            size="sm"
                            border={buttonBorderColor}
                            color={buttonTextColor}
                            onPress={retry}
                        >
                            {tShared("retry")}
                        </Button>
                    }
                </Flex>
            </Flex>
        </Flex>
    );
};
